import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type { QuizScoresAveragesDataItem } from 'teacher-ui-client/components/giraffe/charts/quiz-scores/averages'
import type { SchoolScope, StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'

export interface QuizScoresAveragesResponse {
  quizzes_averages_summary_by_grade: SummaryData[]
  quizzes_averages_by_grade: AveragesData[]
}

interface SummaryData {
  grade_position: number
  data: [
    {
      category: 'average_attempts'
      count: number
    },
    {
      category: 'average_score'
      count: number
    },
    {
      category: 'scores'
      values: [
        {
          range: '0-49'
          student_count: number
        },
        {
          range: '50-79'
          student_count: number
        },
        {
          range: '80-100'
          student_count: number
        },
      ]
    },
  ]
}

interface AveragesData {
  grade_position: number
  lessons: {
    lesson: number
    attempts: number
    average_score: number
    student_count: number
  }[]
}

export async function quizScoresAverages(
  this: StudentEventsService,
  product: LegacyProductRex,
  statsScope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<QuizScoresAveragesDataItem[]>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('quizzes_averages_by_grade', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<QuizScoresAveragesResponse>(url, query)
  if (!data) return null

  const { quizzes_averages_summary_by_grade: summaryData, quizzes_averages_by_grade: averagesData } = data
  if (summaryData.length === 0 || averagesData.length === 0) return null

  return groupByGrade(summaryData, averagesData)
}

function groupByGrade(summaryData: SummaryData[], averagesData: AveragesData[]) {
  const sortedSummaryData = [...summaryData].sort((a, b) => a.grade_position - b.grade_position)

  return sortedSummaryData
    .map((summary) => {
      const averages = averagesData.find((average) => average.grade_position === summary.grade_position)
      if (!averages) return null

      const average_attempts = summary.data.find((summaryItem) => summaryItem.category === 'average_attempts')!.count
      const average_score = summary.data.find((summaryItem) => summaryItem.category === 'average_score')!.count
      const scores = summary.data.find((summaryItem) => summaryItem.category === 'scores')!.values

      return {
        grade_position: summary.grade_position,
        summary: {
          average_attempts,
          average_score,
          scores,
        },
        lessons: averages.lessons,
      }
    })
    .filter((data) => data !== null)
}
